import React, { useState } from 'react';
import { ChevronDown, Star, Clock, Users } from 'lucide-react';

/**
 * DoctorSummaryCard — Collapsible card showing doctor info.
 * Used in BookingForm and MembershipOffer so the user always knows
 * which doctor is assigned to their appointment.
 *
 * Props:
 *   doctor        — object with { name, last_name, avatar, specialty, rating_professional,
 *                    attended_users, consultation_minutes, tag_ids }
 *   compact       — if true, render a smaller, more subtle version (for MembershipOffer)
 *   defaultOpen   — whether details section starts expanded
 */
const DoctorSummaryCard = ({ doctor, compact = false, defaultOpen = false }) => {
  const [expanded, setExpanded] = useState(defaultOpen);

  if (!doctor) return null;

  const fullName = `${doctor.name || ''} ${doctor.last_name || ''}`.trim();
  const specialtyName = doctor.specialty?.name || '';
  const experienceHours = Math.round((doctor.consultation_minutes || 0) / 60);
  const avatarFallback = `https://ui-avatars.com/api/?name=${encodeURIComponent(fullName)}&background=786ED9&color=fff&size=112`;

  if (compact) {
    return (
      <div className="animate-fadeIn">
        <button
          type="button"
          onClick={() => setExpanded(prev => !prev)}
          className="w-full flex items-center gap-3 p-3 rounded-xl bg-brand-50 border border-brand-100 hover:bg-brand-tint transition-all group"
        >
          <img
            src={doctor.avatar || avatarFallback}
            alt={fullName}
            className="w-9 h-9 rounded-full object-cover border-2 border-white shadow-soft flex-shrink-0"
            onError={(e) => { e.target.src = avatarFallback; }}
          />
          <div className="flex-1 text-left min-w-0">
            <p className="text-sm font-semibold text-text-primary truncate">{fullName}</p>
            {specialtyName && (
              <p className="text-xs text-text-secondary truncate">{specialtyName}</p>
            )}
          </div>
          <ChevronDown
            size={16}
            className={`text-text-secondary transition-transform duration-300 flex-shrink-0 ${expanded ? 'rotate-180' : ''}`}
          />
        </button>

        {expanded && (
          <div className="mt-2 p-3 rounded-xl bg-surface-card border border-brand-100 animate-slideDown">
            <DoctorDetails
              doctor={doctor}
              fullName={fullName}
              avatarFallback={avatarFallback}
              experienceHours={experienceHours}
              small
            />
          </div>
        )}
      </div>
    );
  }

  // ── Standard version (BookingForm) ──
  return (
    <div className="animate-fadeIn">
      <button
        type="button"
        onClick={() => setExpanded(prev => !prev)}
        className="w-full flex items-center gap-3 p-3 rounded-xl bg-brand-50 border border-brand-100 hover:bg-brand-tint transition-colors group"
      >
        <img
          src={doctor.avatar || avatarFallback}
          alt={fullName}
          className="w-12 h-12 rounded-full object-cover border-2 border-white shadow-soft flex-shrink-0"
          onError={(e) => { e.target.src = avatarFallback; }}
        />
        <div className="flex-1 text-left min-w-0">
          <p className="text-xs text-text-secondary font-medium mb-0.5">Tu doctora</p>
          <p className="text-sm font-bold text-text-primary truncate">{fullName}</p>
          {specialtyName && (
            <span className="inline-block mt-0.5 px-2 py-0.5 bg-brand-tint text-brand-deep rounded-full text-[10px] font-semibold">
              {specialtyName}
            </span>
          )}
        </div>
        <div className="flex flex-col items-center gap-1 flex-shrink-0">
          {doctor.rating_professional && (
            <div className="flex items-center gap-0.5">
              <Star className="text-amber-pill fill-amber-pill" size={12} />
              <span className="text-xs font-bold text-text-primary">{doctor.rating_professional}</span>
            </div>
          )}
          <ChevronDown
            size={16}
            className={`text-text-secondary transition-transform duration-300 ${expanded ? 'rotate-180' : ''}`}
          />
        </div>
      </button>

      {expanded && (
        <div className="mt-2 p-4 rounded-xl bg-surface-card border border-brand-100 animate-slideDown">
          <DoctorDetails
            doctor={doctor}
            fullName={fullName}
            avatarFallback={avatarFallback}
            experienceHours={experienceHours}
          />
        </div>
      )}
    </div>
  );
};

/**
 * Inner details section shown when the card is expanded.
 */
const DoctorDetails = ({ doctor, fullName, avatarFallback, experienceHours, small = false }) => {
  return (
    <div className={`space-y-3 ${small ? 'text-xs' : 'text-sm'}`}>
      {/* Stats row */}
      <div className="flex items-center gap-4 flex-wrap">
        {doctor.rating_professional && (
          <div className="flex items-center gap-1.5">
            <div className="bg-amber-pill/30 p-1 rounded-md">
              <Star className="text-amber-pill fill-amber-pill" size={small ? 12 : 14} />
            </div>
            <div>
              <span className="font-bold text-text-primary">{doctor.rating_professional}</span>
              <span className="text-text-secondary ml-1">rating</span>
            </div>
          </div>
        )}

        {(doctor.attended_users > 0) && (
          <div className="flex items-center gap-1.5">
            <div className="bg-brand-tint p-1 rounded-md">
              <Users className="text-brand" size={small ? 12 : 14} />
            </div>
            <div>
              <span className="font-bold text-text-primary">{doctor.attended_users}</span>
              <span className="text-text-secondary ml-1">pacientes</span>
            </div>
          </div>
        )}

        {experienceHours > 0 && (
          <div className="flex items-center gap-1.5">
            <div className="bg-brand-tint p-1 rounded-md">
              <Clock className="text-brand" size={small ? 12 : 14} />
            </div>
            <div>
              <span className="font-bold text-text-primary">{experienceHours}</span>
              <span className="text-text-secondary ml-1">hrs exp.</span>
            </div>
          </div>
        )}
      </div>

      {/* Tags */}
      {doctor.tag_ids && doctor.tag_ids.length > 0 && (
        <div>
          <p className={`font-medium text-text-secondary mb-1.5 ${small ? 'text-[10px]' : 'text-xs'}`}>
            Especialista en:
          </p>
          <div className="flex flex-wrap gap-1.5">
            {doctor.tag_ids.slice(0, 4).map((tag, index) => (
              <span
                key={index}
                className={`px-2 py-0.5 bg-brand-tint text-brand-deep rounded-full font-medium ${small ? 'text-[10px]' : 'text-xs'}`}
              >
                {tag.name}
              </span>
            ))}
            {doctor.tag_ids.length > 4 && (
              <span className={`px-2 py-0.5 bg-brand-50 text-text-mute rounded-full ${small ? 'text-[10px]' : 'text-xs'}`}>
                +{doctor.tag_ids.length - 4} más
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DoctorSummaryCard;
